import { z } from 'zod'
import { nonEmpty, optionalText, pageQuery, uuid } from './common.ts'

// Quantity, not price. What a thing costs is the price list's business,
// and a doctor should not need to see prices to write a prescription.
//
// doseDate set  -> a dated dose in the schedule. The same product may
//                  appear on several dates with a different quantity each
//                  time, which is the point.
// doseDate null -> a general medicine, dispensed once.
export const prescriptionLine = z.object({
  productId: uuid,
  /** The dose taken on EACH occasion, not the daily total. */
  quantity: z.coerce.number().positive().max(9999),
  /**
   * Occasions per day: 1 once, 2 morning and evening, 3 thrice.
   * Defaults to 1 so a caller that predates this field still means
   * "quantity, once" and nothing it sent changes value.
   */
  dosesPerDay: z.coerce.number().int().min(1).max(4).default(1),
  doseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
})

/** What is actually taken in a day, which is what billing charges for. */
export const dailyTotal = (line: { quantity: number; dosesPerDay?: number }): number =>
  line.quantity * (line.dosesPerDay ?? 1)

export const TIMES_PER_DAY = [
  { value: 1, label: 'Once daily', short: 'once' },
  { value: 2, label: 'Twice daily (morning and evening)', short: 'twice' },
  { value: 3, label: 'Three times daily', short: 'thrice' },
  { value: 4, label: 'Four times daily', short: '4×' },
] as const
export type PrescriptionLine = z.infer<typeof prescriptionLine>

export const createPrescription = z.object({
  id: uuid,
  patientId: uuid,
  doctorStaffId: uuid,
  remarks: optionalText(4000),
  lines: z.array(prescriptionLine).min(1, 'Add at least one product').max(400),
})
export type CreatePrescription = z.infer<typeof createPrescription>

export const updatePrescription = z.object({
  patientId: uuid.optional(),
  doctorStaffId: uuid.optional(),
  remarks: optionalText(4000),
  // Sent whole rather than as a diff: a prescription is read as one
  // document, and reconciling per-line edits invites a half-changed list.
  lines: z.array(prescriptionLine).min(1).max(400).optional(),
  version: z.number().int().positive(),
})
export type UpdatePrescription = z.infer<typeof updatePrescription>

// Search-as-you-type. Deliberately not "list everything": these tables
// grow without limit and a dropdown that loads all of them stops working
// exactly when the shop gets busy.
export const lookupQuery = pageQuery.pick({ q: true }).extend({
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(25).default(10),
})

export const prescriptionFilters = pageQuery.extend({
  patientId: uuid.optional(),
  doctorStaffId: uuid.optional(),
})

export const PRESCRIPTION_STATUSES = ['draft', 'sent'] as const
export type PrescriptionStatus = (typeof PRESCRIPTION_STATUSES)[number]

/** Hands the prescription to the counter and opens its bill. */
export const sendToBilling = z.object({
  version: z.number().int().positive(),
})

export const RX_MIN_LINES = 1
export const rxLineDraft = z.object({ productId: z.string(), quantity: z.string() })
export type RxLineDraft = z.infer<typeof rxLineDraft>
export const _unusedNonEmpty = nonEmpty
